<template>
  <v-dialog :value="value" max-width="560" persistent @input="$emit('input', $event)">
    <v-card class="rpd-save-dialog">
      <v-card-title class="dialog__title">
        <span>{{ state.title }}</span>
      </v-card-title>

      <div class="dialog__banner" :class="state.cls">
        <i class="material-icons" aria-hidden="true">{{ state.icon }}</i>
        <p>{{ detail }}</p>
      </div>

      <v-card-text>
        <div class="field">
          <label for="rpd-save-name">Query name</label>
          <v-text-field
            id="rpd-save-name"
            :value="name"
            autofocus
            hide-details
            @input="name = $event"
          />
        </div>

        <div class="field">
          <label for="rpd-save-description">Description</label>
          <v-text-field
            id="rpd-save-description"
            :value="description"
            hide-details
            @input="description = $event"
          />
        </div>

        <div class="field field--inline">
          <v-checkbox :input-value="isShared" hide-details label="Share with everyone in this tenant" @change="isShared = $event" />
        </div>
        <p class="field-note">
          A shared query can be reopened by any user in this tenant. Deleting it stays with the
          owner.
        </p>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn flat :disabled="busy" @click="$emit('input', false)">Cancel</v-btn>
        <v-btn :class="state.button" :disabled="!canSave" :loading="busy" @click="submit">
          {{ state.label }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import {
    saveState,
    saveStateDetail,
    savedQueryFor,
  } from '@/helpers/rpd'

  /*
   * The save dialog.
   *
   * The three collision states are derived from the list the drawer already holds, so opening the
   * dialog costs no round trip. The server's 409 is the race fallback for a name taken between the
   * list load and the write; the caller re-reads the list and the banner re-renders from it.
   */
  export default {
    name: 'RpdSaveQueryDialog',

    props: {
      value: {
        type: Boolean,
        default: false,
      },
      savedQueries: {
        type: Array,
        default: () => [],
      },
      currentUserId: {
        type: String,
        default: '',
      },
      initialName: {
        type: String,
        default: '',
      },
      initialShared: {
        type: Boolean,
        default: false,
      },
      initialDescription: {
        type: String,
        default: '',
      },
      busy: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        name: this.initialName,
        isShared: this.initialShared,
        description: this.initialDescription,
      }
    },

    computed: {
      existing() {
        return savedQueryFor(this.name, this.savedQueries)
      },

      state() {
        return saveState(this.name, this.savedQueries, this.currentUserId)
      },

      detail() {
        return saveStateDetail(this.state, this.existing)
      },

      canSave() {
        return !!this.name.trim() && !this.busy
      },
    },

    watch: {
      value(isOpen) {
        /* Reopening must not resurrect the previous draft. */
        if (isOpen) {
          this.name = this.initialName
          this.isShared = this.initialShared
          this.description = this.initialDescription
        }
      },
    },

    methods: {
      submit() {
        if (!this.canSave) return

        this.$emit('save', {
          name: this.name.trim(),
          isShared: this.isShared,
          description: this.description.trim(),
          state: this.state,
          existing: this.existing,
        })
      },
    },
  }
</script>
